import React from 'react';
import { T } from '../../lib/tokens';

type TabId = 'receipts' | 'upload' | 'rules' | 'settings';

interface Props {
  active: TabId;
  onChange: (tab: TabId) => void;
  reviewCount?: number;
}

const TabIcon = ({ id, size = 26, color, filled }: { id: TabId; size?: number; color: string; filled: boolean }) => {
  if (id === 'receipts') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 3H16L19 6V21L16 19.5L14 21L12 19.5L10 21L8 19.5L6 21Z" stroke={color} strokeWidth={filled ? 0 : 1.8} strokeLinejoin="round" fill={filled ? color : 'none'} />
      <path d="M9 8H15M9 11H13" stroke={filled ? T.paper : color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
  if (id === 'upload') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="7" width="18" height="13" rx="3" stroke={color} strokeWidth={filled ? 0 : 1.8} fill={filled ? color : 'none'} />
      <circle cx="12" cy="13.5" r="3.5" stroke={filled ? T.paper : color} strokeWidth="1.8" fill="none" />
      <path d="M8 7L9 4H15L16 7" stroke={color} strokeWidth={filled ? 0 : 1.8} strokeLinejoin="round" fill={filled ? color : 'none'} />
    </svg>
  );
  if (id === 'rules') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke={color} strokeWidth={filled ? 0 : 1.8} fill={filled ? color : 'none'} />
      <path d="M7 9H11M7 13H15M7 17H13" stroke={filled ? T.paper : color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" stroke={color} strokeWidth={filled ? 0 : 1.8} fill={filled ? color : 'none'} />
      <path d="M12 3L13 6L16 5L15 8L18 9L15 11L17 14L14 13L13 16L12 13L11 16L10 13L7 14L9 11L6 9L9 8L8 5L11 6Z" stroke={color} strokeWidth={filled ? 0 : 1.5} strokeLinejoin="round" fill={filled ? color : 'none'} />
    </svg>
  );
};

export function BottomTabBar({ active, onChange, reviewCount = 0 }: Props) {
  const tabs: Array<{ id: TabId; label: string; badge?: number }> = [
    { id: 'receipts', label: 'Receipts', badge: reviewCount },
    { id: 'upload', label: 'Upload' },
    { id: 'rules', label: 'Rules' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: `1px solid ${T.paperLine}`,
        display: 'flex',
        paddingBottom: 'calc(max(env(safe-area-inset-bottom), 12px) + 16px)',
        paddingTop: 8,
        flexShrink: 0,
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const color = isActive ? T.navy : T.graphiteDim;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              flex: 1,
              background: 'transparent',
              border: 0,
              padding: '6px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              cursor: 'pointer',
              fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              position: 'relative',
            }}
          >
            <div style={{ position: 'relative' }}>
              <TabIcon id={tab.id} color={color} filled={isActive} />
              {(tab.badge ?? 0) > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -8,
                    background: T.warn,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 600,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 100,
                    padding: '0 4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </div>
            <span style={{ fontSize: 10, fontWeight: 500, color }}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export type { TabId };
